import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { SdiHeart, SdiLicense, SdirFooter, SdirHeader, SkipLink, styles } from '@sdir/sds';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { DEFAULT_LANG, getLanguageOptions } from '../../../helpers/languageHelpers';
import { routes } from '../../../constants/routes';
import { useFeatureToggles } from '../../../services/hooks/useFeatureToggles';
import { ToggleDisableDispensation, ToggleDisableSid } from '../../../constants/permissions';

type AppLayoutProps = {
  children: any;
  onChangeLanguage: (l: string) => void;
};

const AppLayout: React.FC<AppLayoutProps> = ({ children, onChangeLanguage }) => {
  const intl = useIntl();
  const featureToggle = useFeatureToggles();
  const enabledSid = !isToggleOn(featureToggle?.result, ToggleDisableSid, '');
  const enabledDispensation = !isToggleOn(featureToggle?.result, ToggleDisableDispensation, '');

  const languageOptions = useMemo(() => {
    return getLanguageOptions();
  }, []);

  const aboutLinks = useMemo(() => {
    return [
      {
        text: intl.formatMessage({ id: 'footer.about.privacy' }),
        url: 'https://www.sdir.no/om-oss/personvernerklaring/',
      },
      {
        text: intl.formatMessage({ id: 'footer.about.accessibility' }),
        url: 'https://uustatus.no/nb/erklaringer/publisert/78f546ff-ec49-4bd1-a5c6-6d8cd10c4c97',
        type: 'external' as any,
      },
    ];
  }, [intl]);

  const socialLinks = useMemo(() => {
    return [
      {
        text: intl.formatMessage({ id: 'footer.social.facebook' }),
        url: 'https://www.facebook.com/sjofartsdir',
        type: 'facebook' as any,
      },
      {
        text: intl.formatMessage({ id: 'footer.social.instagram' }),
        url: 'https://www.instagram.com/sjofartsdir/',
        type: 'instagram' as any,
      },
      {
        text: intl.formatMessage({ id: 'footer.social.linkedin' }),
        url: 'https://www.linkedin.com/company/41031/',
        type: 'linkedin' as any,
      },
    ];
  }, [intl]);

  const menuItems: any[] = useMemo(() => {
    const items: any[] = [];
    const urlPrefix = intl?.locale !== DEFAULT_LANG ? `/${intl.locale}` : '';
    items.push({
      icon: <SdiLicense color={styles.colors.primary} size="s" />,
      text: intl.formatMessage({ id: 'certificate.search.header' }),
      type: 'a',
      url: `#${urlPrefix}${routes.certificateSearch}`,
    });
    items.push({
      icon: <SdiLicense color={styles.colors.primary} size="s" />,
      text: intl.formatMessage({ id: 'endorsement.search.header' }),
      type: 'a',
      url: `#${urlPrefix}${routes.endorsementSearch}`,
    });
    if (enabledDispensation) {
      items.push({
        icon: <SdiLicense color={styles.colors.primary} size="s" />,
        text: intl.formatMessage({ id: 'dispensation.search.header' }),
        type: 'a',
        url: `#${urlPrefix}${routes.dispensationSearch}`,
      });
    }
    if (enabledSid) {
      items.push({
        icon: <SdiLicense color={styles.colors.primary} size="s" />,
        text: intl.formatMessage({ id: 'sid.search.header' }),
        type: 'a',
        url: `#${urlPrefix}${routes.sidSearch}`,
      });
    }
    items.push({
      icon: <SdiHeart color={styles.colors.primary} size="s" />,
      text: intl.formatMessage({ id: 'health.search.header' }),
      type: 'a',
      url: `#${urlPrefix}${routes.healthSearch}`,
    });
    return items;
  }, [intl, enabledSid, enabledDispensation]);

  return (
    <>
      <SkipLink
        linkText={intl.formatMessage({ id: 'userbar.skiplink' })}
        mainContentId="app-main"
      />
      <SdirHeader
        appName={intl.formatMessage({ id: 'home.page.title' })}
        backLink={{
          url: 'https://sdir.no',
          text: intl.formatMessage({ id: 'userbar.sdirlink' }),
        }}
        maxContentWidth="1490px"
        menuToggleText={intl.formatMessage({ id: 'userbar.menu' })}
        navMenuItems={menuItems}
        languageOptions={languageOptions}
        onChangeLanguage={onChangeLanguage}
        selectedLanguage={intl.locale || DEFAULT_LANG}
      />
      <AppContainer id="app-main" data-testid="verify-app-container" tabIndex={-1}>
        {children}
      </AppContainer>
      <SdirFooter
        tagline={intl.formatMessage({ id: 'footer.tagline' })}
        maxContentWidth="1490px"
        languageOptions={languageOptions}
        onChangeLanguage={onChangeLanguage}
        selectedLanguage={intl.locale || DEFAULT_LANG}
        aboutHeader={intl.formatMessage({ id: 'footer.about.title' })}
        aboutLinks={aboutLinks}
        socialHeader={intl.formatMessage({ id: 'footer.social.title' })}
        socialLinks={socialLinks}
      />
    </>
  );
};

export default AppLayout;

const AppContainer = styled.main`
  background-color: ${styles.colors.background};
  margin: 0 auto 0 auto;
  max-width: 1490px;
  min-height: 700px;
  padding: 20px 0;

  @media screen and (max-width: ${({ theme }) => theme.screen.xLarge}) {
    padding: 0 15px 15px 15px;
  }

  footer ul {
    padding-left: 0;
  }
`;
